import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnInit,
	Output,
	SimpleChanges,
	ViewEncapsulation,
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { combineLatest, Observable, of, Subject } from "rxjs";
import { filter, map, mergeMap, takeUntil } from "rxjs/operators";
import { translateAndFormat } from "src/app/i18next";
import { DisplayedZone, MirRule } from "src/app/models/mir-rule.model";
import { RulesService } from "src/app/services/rules/rules.service";
import { ZoneNamesService } from "src/app/services/zone-names/zone-names.service";
import { ZonesService } from "src/app/services/zones.service";
import { isDefined } from "src/utils/isDefined/isDefined";
import { ConfirmEditRuleDialogComponent } from "../confirm-edit-rule-dialog/confirm-edit-rule-dialog.component";
@Component({
	selector: "app-rule-summary",
	templateUrl: "./rule-summary.component.html",
	styleUrls: ["./rule-summary.component.scss"],
	encapsulation: ViewEncapsulation.None,
})
export class RuleSummaryComponent implements OnInit, OnChanges {
	constructor(
		private dialog: MatDialog,
		private zonenNamesService: ZoneNamesService,
		private rulesService: RulesService,
		private zonesService: ZonesService,
	) {}

	@Input() rule: MirRule = null;
	@Input() view: string = null;
	@Output() closeBtnClicked = new EventEmitter();
	@Output() ruleAfterPatched: EventEmitter<MirRule> = new EventEmitter();
	excludedZones$: Observable<string> = of(null);
	startTripZones$: Observable<string> = of(null);
	endTripZones$: Observable<string> = of(null);
	excludedZonesLoading = true;
	startTripLoading = true;
	endTripLoading = true;
	showTripDetails = false;

	destroy$ = new Subject();

	divisionId: string = null;

	ngOnInit(): void {
		this.zonenNamesService.categoryId$
			.pipe(
				filter(isDefined),
				takeUntil(this.destroy$),
				mergeMap(catId => {
					return this.zonenNamesService.getCategoryById(catId);
				}),
			)
			.subscribe(cat => {
				// will cause breaking changes if Core API decides that more than 1 div can be in category's divisions array
				this.divisionId = cat.divisions[0];
				this.zonesService.divisionId$.next(this.divisionId);
			});
	}

	private _setZonesToChecked(zones: DisplayedZone[], divisionId: string) {
		return zones.map(z => {
			return {
				...z,
				checked: true,
				divisionId,
			};
		});
	}

	setZones() {
		// excluded zones
		if (this.rule?.excludedZones?.length) {
			const zoneIds = this.rule.excludedZones.map(z => z.zoneId);
			this.excludedZones$ = combineLatest([
				this.zonenNamesService.getZoneNames(zoneIds),
				this.zonesService.divisionId$,
			]).pipe(
				map(([{ zoneNames, zones }, divisionId]) => {
					this.excludedZonesLoading = false;
					this.updateSummaryVisibility();
					this.zonesService.selectedZonesExcluded$.next(this._setZonesToChecked(zones, divisionId));
					this.rule.detailedExcludedZones = this._setZonesToChecked(zones, divisionId);
					return zoneNames;
				}),
			);
		} else {
			this.excludedZones$ = of(translateAndFormat("none", "capitalize"));
			this.excludedZonesLoading = false;
			this.updateSummaryVisibility();
		}

		// trip start zones
		if (this.rule?.tripStart?.zones?.length) {
			const zoneIds = this.rule.tripStart.zones.map(z => z.zoneId);
			this.startTripZones$ = combineLatest([
				this.zonenNamesService.getZoneNames(zoneIds),
				this.zonesService.divisionId$,
			]).pipe(
				map(([{ zoneNames, zones }, divisionId]) => {
					this.startTripLoading = false;
					this.updateSummaryVisibility();
					this.zonesService.selectedZonesStart$.next(this._setZonesToChecked(zones, divisionId));
					this.rule.tripStart.detailedZones = this._setZonesToChecked(zones, divisionId);

					return zoneNames;
				}),
			);
		} else {
			this.startTripZones$ = of(translateAndFormat("none", "capitalize"));
			this.startTripLoading = false;
			this.updateSummaryVisibility();
		}

		// trip end zones
		if (this.rule.tripEnd?.zones?.length) {
			const zoneIds = this.rule.tripEnd.zones.map(z => z.zoneId);

			this.endTripZones$ = combineLatest([
				this.zonenNamesService.getZoneNames(zoneIds),
				this.zonesService.divisionId$,
			]).pipe(
				map(([{ zoneNames, zones }, divisionId]) => {
					this.endTripLoading = false;
					this.updateSummaryVisibility();
					this.zonesService.selectedZonesEnd$.next(this._setZonesToChecked(zones, divisionId));
					this.rule.tripEnd.detailedZones = this._setZonesToChecked(zones, divisionId);

					return zoneNames;
				}),
			);
		} else {
			this.endTripZones$ = of(translateAndFormat("none", "capitalize"));
			this.endTripLoading = false;
			this.updateSummaryVisibility();
		}

		this.updateSummaryVisibility();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes?.rule) {
			// allow change detection cycle to complete
			setTimeout(() => {
				this.zonesService.reset();
			});

			// Handle the rule change regardless of whether it's truthy or falsy
			this.rule = changes.rule.currentValue;

			if (this.rule) {
				this.setZones();
			}
		} else {
			this.rule = null;
		}
	}

	private updateSummaryVisibility() {
		// Check if all zone data is ready (either loaded with data or set to "None")
		const excludedZonesReady = !this.excludedZonesLoading;
		const startTripZonesReady = !this.startTripLoading;
		const endTripZonesReady = !this.endTripLoading;

		this.showTripDetails = excludedZonesReady && startTripZonesReady && endTripZonesReady;
	}

	setHeaderText() {
		return translateAndFormat("rule summary", "capitalize");
	}

	closeButtonClick() {
		this.rule = null;
		// prevents a prepopulated edit rule
		this.rulesService.ruleToPatch$.next(null);
		this.zonesService.reset();
		this.closeBtnClicked.emit(null);
	}

	editButtonClick() {
		this.rulesService.ruleToPatch$.next(this.rule);
		if (this.rule.assets.length > 0) {
			this.dialog.open(ConfirmEditRuleDialogComponent, {
				data: this.rule.assets.length,
				autoFocus: true,
				restoreFocus: true,
			});
		} else {
			this.rulesService.confirmEditRule$.next();
		}
	}
}
